import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaPaperclip, FaTrash } from "react-icons/fa";
import EmailComposer from "../../components/EmailComposer";
import EmailRichTextEditor from "../../components/EmailRichTextEditor";
import Pagination from "../../components/Pagination";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import StyledSelect from "../../components/StyledSelect";
import useAccess from "../../hooks/useAccess";
import "../../styles/Communications.css";
import "../../styles/Reports.css";
const ITEMS_PER_PAGE = 10;
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});
const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-CA", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "-";
const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "Size unavailable";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
const emailBodyText = (value) => {
  if (!value) return "-";
  const documentNode = new DOMParser().parseFromString(value, "text/html");
  return documentNode.body.textContent?.trim() || "-";
};
const sortValue = (value) => {
  if (value == null) return "";
  if (value instanceof Date) return value.getTime();
  return typeof value === "number" ? value : String(value).toLowerCase();
};
function SortableHeader({ label, column, sort, onSort }) {
  const active = sort.column === column;
  return (
    <th aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="nw-sort-button" onClick={() => onSort(column)}>
        <span>{label}</span>
        <span className={`nw-sort-indicator ${active ? `is-${sort.direction}` : ""}`} aria-hidden="true">
          <i className="fa-solid fa-arrow-up" />
        </span>
      </button>
    </th>
  );
}
export function ComposeEmailPage() {
  const navigate = useNavigate();
  return (
    <div className="communications-page">
      <header className="communications-page-header">
        <div className="communications-heading">
          <span className="communications-title-icon" aria-hidden="true">
            <i className="fa fa-paper-plane" />
          </span>
          <div>
            <span className="communications-eyebrow">Communications</span>
            <h2>Compose Email</h2>
            <p>Prepare a message, select recipients, and review it before sending.</p>
          </div>
        </div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/communications")}
        >
          Back to Email History
        </button>
      </header>
      <EmailComposer showDirectory />
    </div>
  );
}
export default function CommunicationsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAccess();
  const canEditEmail = hasPermission("Communications", "Email", "edit");
  const canEditCommunications = hasPermission(
    "Communications",
    "Communications",
    "edit",
  );
  const canDeleteCommunications = hasPermission(
    "Communications",
    "Communications",
    "delete",
  );
  const [tab, setTab] = useState("history");
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [template, setTemplate] = useState({
    title: "",
    subject: "",
    body: "",
  });
  const [feedback, setFeedback] = useState("");
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({ search: "", status: "", startDate: "", endDate: "" });
  const [templateFilters, setTemplateFilters] = useState({ search: "", createdBy: "" });
  const [historySort, setHistorySort] = useState({ column: "sentAt", direction: "desc" });
  const [templateSort, setTemplateSort] = useState({ column: "createdAt", direction: "desc" });
  const [historyPage, setHistoryPage] = useState(1);
  const [templatePage, setTemplatePage] = useState(1);

  useEffect(() => { setHistoryPage(1); }, [historyFilters]);
  useEffect(() => { setTemplatePage(1); }, [templateFilters]);
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [historyResponse, templateResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/communications/history`, {
          headers: headers(),
        }),
        axios.get(`${API_BASE}/api/communications/templates`, {
          headers: headers(),
        }),
      ]);
      setHistory(historyResponse.data || []);
      setTemplates(templateResponse.data || []);
    } catch (error) {
      setFeedback(
        error.response?.data?.message || "Failed to load communications.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    if (!selectedHistory && !selectedTemplate) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedHistory(null);
        setSelectedTemplate(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedHistory, selectedTemplate]);
  const createTemplate = async () => {
    setFeedback("");
    try {
      await axios.post(`${API_BASE}/api/communications/templates`, template, {
        headers: headers(),
      });
      setTemplate({ title: "", subject: "", body: "" });
      setShowTemplateForm(false);
      await loadData();
    } catch (error) {
      setFeedback(
        error.response?.data?.message || "Failed to create template.",
      );
    }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this email template?")) return;
    await axios.delete(`${API_BASE}/api/communications/templates/${id}`, {
      headers: headers(),
    });
    loadData();
  };
  const historyStatuses = useMemo(
    () => [...new Set(history.map((email) => email.status).filter(Boolean))].sort(),
    [history],
  );
  const templateCreators = useMemo(
    () => [...new Set(templates.map((item) => item.createdBy || "Administrator"))].sort(),
    [templates],
  );
  const filteredHistory = useMemo(() => {
    const query = historyFilters.search.trim().toLowerCase();
    return history.filter((email) => {
      const sentAt = email.sentAt || email.createdAt;
      const day = sentAt ? new Date(sentAt).toISOString().slice(0, 10) : "";
      const searchable = [
        ...(email.recipients || []), email.createdBy, email.status, email.replyTo,
        email.fromAddresses, email.subject,
        ...(email.attachments || []).map((file) => file.name),
      ].filter(Boolean).join(" ").toLowerCase();
      return (!query || searchable.includes(query))
        && (!historyFilters.status || email.status === historyFilters.status)
        && (!historyFilters.startDate || day >= historyFilters.startDate)
        && (!historyFilters.endDate || day <= historyFilters.endDate);
    });
  }, [history, historyFilters]);
  const sortedHistory = useMemo(() => [...filteredHistory].sort((left, right) => {
    const values = {
      sentAt: (item) => new Date(item.sentAt || item.createdAt || 0).getTime(),
      recipients: (item) => (item.recipients || []).join(", "),
      createdBy: (item) => item.createdBy || "Administrator",
      status: (item) => item.status,
      replyTo: (item) => item.replyTo,
      from: (item) => item.fromAddresses,
      subject: (item) => item.subject,
      attachments: (item) => (item.attachments || []).map((file) => file.name).join(", "),
    };
    const getter = values[historySort.column];
    const a = sortValue(getter(left));
    const b = sortValue(getter(right));
    if (a === b) return 0;
    return (a < b ? -1 : 1) * (historySort.direction === "asc" ? 1 : -1);
  }), [filteredHistory, historySort]);
  const filteredTemplates = useMemo(() => {
    const query = templateFilters.search.trim().toLowerCase();
    return templates.filter((item) => {
      const creator = item.createdBy || "Administrator";
      const searchable = [item.title, item.subject, emailBodyText(item.body), creator].filter(Boolean).join(" ").toLowerCase();
      return (!query || searchable.includes(query))
        && (!templateFilters.createdBy || creator === templateFilters.createdBy);
    });
  }, [templates, templateFilters]);
  const sortedTemplates = useMemo(() => [...filteredTemplates].sort((left, right) => {
    const values = {
      title: (item) => item.title,
      subject: (item) => item.subject,
      body: (item) => emailBodyText(item.body),
      createdBy: (item) => item.createdBy || "Administrator",
      createdAt: (item) => new Date(item.createdAt || 0).getTime(),
    };
    const getter = values[templateSort.column];
    const a = sortValue(getter(left));
    const b = sortValue(getter(right));
    if (a === b) return 0;
    return (a < b ? -1 : 1) * (templateSort.direction === "asc" ? 1 : -1);
  }), [filteredTemplates, templateSort]);
  const pagedHistory = sortedHistory.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE);
  const pagedTemplates = sortedTemplates.slice((templatePage - 1) * ITEMS_PER_PAGE, templatePage * ITEMS_PER_PAGE);
  const changeSort = (setter, pageSetter) => (column) => {
    setter((current) => ({
      column,
      direction: current.column === column && current.direction === "asc" ? "desc" : "asc",
    }));
    pageSetter(1);
  };
  return (
    <div className="communications-page">
      <header className="communications-page-header">
        <div className="communications-heading">
          <span className="communications-title-icon" aria-hidden="true">
            <i className="fa fa-comments" />
          </span>
          <div>
            <span className="communications-eyebrow">Administration</span>
            <h2>Communications</h2>
            <p>Review sent emails and manage reusable email templates.</p>
          </div>
        </div>
        {tab === "history" && canEditEmail ? (
          <button
            className="btn btn-primary"
            onClick={() => navigate("/communications/compose")}
          >
            Send New Email <i className="fa fa-paper-plane" />
          </button>
        ) : tab === "templates" && canEditCommunications ? (
          <button
            className="btn btn-primary"
            onClick={() => setShowTemplateForm((value) => !value)}
          >
            Create New Template <i className="fa fa-plus" />
          </button>
        ) : null}
      </header>
      <section className="communications-workspace">
      <nav className="communications-tabs" role="tablist" aria-label="Communications views">
        <button
          type="button"
          data-no-standard-button="true"
          className={`communications-tab ${tab === "history" ? "active" : ""}`}
          role="tab"
          aria-selected={tab === "history"}
          onClick={() => setTab("history")}
        >
          <i className="fa fa-envelope" /> Email History
          <span className="communications-tab-count">{history.length}</span>
        </button>
        <button
          type="button"
          data-no-standard-button="true"
          className={`communications-tab ${tab === "templates" ? "active" : ""}`}
          role="tab"
          aria-selected={tab === "templates"}
          onClick={() => setTab("templates")}
        >
          <i className="fa fa-file-text" /> Email Templates
          <span className="communications-tab-count">{templates.length}</span>
        </button>
      </nav>
      <div className="communications-workspace-body">
      {feedback ? (
        <div className="communications-feedback error">{feedback}</div>
      ) : null}
      <ReportFilterPanel>
        {tab === "history" ? (
          <div className="communications-filter-grid">
            <div className="communications-filter-field">
              <label htmlFor="communications-history-search">Search</label>
              <input id="communications-history-search" type="search" className="form-control" placeholder="Recipient, subject, sender, attachment..." value={historyFilters.search} onChange={(event) => setHistoryFilters((current) => ({ ...current, search: event.target.value }))} />
            </div>
            <div className="communications-filter-field">
              <label htmlFor="communications-history-status">Status</label>
              <StyledSelect
                value={historyFilters.status}
                minWidth={0}
                ariaLabel="Status filter"
                options={[
                  { value: "", label: "All statuses" },
                  ...historyStatuses.map((status) => ({ value: status, label: status })),
                ]}
                onChange={(value) => setHistoryFilters((current) => ({ ...current, status: value }))}
              />
            </div>
            <div className="communications-filter-field">
              <label htmlFor="communications-history-start-date">Start Date</label>
              <input id="communications-history-start-date" type="date" className="form-control" value={historyFilters.startDate} onChange={(event) => setHistoryFilters((current) => ({ ...current, startDate: event.target.value }))} />
            </div>
            <div className="communications-filter-field">
              <label htmlFor="communications-history-end-date">End Date</label>
              <input id="communications-history-end-date" type="date" className="form-control" value={historyFilters.endDate} onChange={(event) => setHistoryFilters((current) => ({ ...current, endDate: event.target.value }))} />
            </div>
          </div>
        ) : (
          <div className="communications-filter-grid communications-filter-grid--templates">
            <div className="communications-filter-field">
              <label htmlFor="communications-template-search">Search</label>
              <input id="communications-template-search" type="search" className="form-control" placeholder="Template name, subject, or content..." value={templateFilters.search} onChange={(event) => setTemplateFilters((current) => ({ ...current, search: event.target.value }))} />
            </div>
            <div className="communications-filter-field">
              <label htmlFor="communications-template-creator">Created By</label>
              <StyledSelect
                value={templateFilters.createdBy}
                minWidth={0}
                ariaLabel="Created By filter"
                options={[
                  { value: "", label: "All creators" },
                  ...templateCreators.map((creator) => ({ value: creator, label: creator })),
                ]}
                onChange={(value) => setTemplateFilters((current) => ({ ...current, createdBy: value }))}
              />
            </div>
          </div>
        )}
      </ReportFilterPanel>
      {loading ? (
        <div className="communications-empty">Loading communications...</div>
      ) : tab === "history" ? (
        <div className="communications-table-wrap nw-data-table-shell">
          <div className="communications-table-scroll">
          <table className="communications-table nw-data-table" data-no-enhance-table="true">
            <thead>
              <tr>
                <SortableHeader label="Date Sent" column="sentAt" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="Recipients" column="recipients" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="Created By" column="createdBy" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="Status" column="status" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="Reply-To" column="replyTo" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="From" column="from" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="Subject" column="subject" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
                <SortableHeader label="Attachments" column="attachments" sort={historySort} onSort={changeSort(setHistorySort, setHistoryPage)} />
              </tr>
            </thead>
            <tbody>
              {pagedHistory.map((email) => (
                <tr
                  key={email.id}
                  className="communications-history-row"
                  role="button"
                  tabIndex="0"
                  aria-label={`Open email details for ${email.subject}`}
                  onClick={() => setSelectedHistory(email)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedHistory(email);
                    }
                  }}
                >
                  <td>{formatDate(email.sentAt || email.createdAt)}</td>
                  <td title={(email.recipients || []).join(", ")}>
                    {(email.recipients || []).length} —{" "}
                    {(email.recipients || []).join(", ")}
                  </td>
                  <td>{email.createdBy || "Administrator"}</td>
                  <td>
                    <span className={`communication-status ${email.status}`}>
                      {email.status}
                    </span>
                  </td>
                  <td>{email.replyTo || "-"}</td>
                  <td>{email.fromAddresses}</td>
                  <td>{email.subject}</td>
                  <td>
                    {(email.attachments || [])
                      .map((file) => file.name)
                      .join(", ") || "-"}
                  </td>
                </tr>
              ))}
              {!pagedHistory.length ? (
                <tr>
                  <td className="communications-table-empty" colSpan="8">
                    No email history found with the current filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
          <Pagination currentPage={historyPage} totalItems={sortedHistory.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setHistoryPage} scrollToTopOnChange={false} />
        </div>
      ) : (
        <>
          {showTemplateForm ? (
            <section className="template-editor">
              <div className="template-editor-heading">
                <span aria-hidden="true"><i className="fa fa-file-text" /></span>
                <div>
                  <h3>Create Email Template</h3>
                  <p>Save a reusable subject and message for future emails.</p>
                </div>
              </div>
              <div className="template-fields">
                <label>
                  Template Name
                  <input
                    value={template.title}
                    onChange={(event) =>
                      setTemplate((current) => ({
                        ...current,
                        title: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Subject
                  <input
                    value={template.subject}
                    onChange={(event) =>
                      setTemplate((current) => ({
                        ...current,
                        subject: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <EmailRichTextEditor
                value={template.body}
                onChange={(body) =>
                  setTemplate((current) => ({ ...current, body }))
                }
              />
              <div className="template-actions">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowTemplateForm(false)}
                >
                  Cancel
                </button>
                <button className="btn btn-success" onClick={createTemplate}>
                  Save Template
                </button>
              </div>
            </section>
          ) : null}
          <div className="communications-table-wrap nw-data-table-shell">
            <div className="communications-table-scroll">
            <table className="communications-table nw-data-table" data-no-enhance-table="true">
              <thead>
                <tr>
                  <SortableHeader label="Name" column="title" sort={templateSort} onSort={changeSort(setTemplateSort, setTemplatePage)} />
                  <SortableHeader label="Subject Line" column="subject" sort={templateSort} onSort={changeSort(setTemplateSort, setTemplatePage)} />
                  <SortableHeader label="Content Preview" column="body" sort={templateSort} onSort={changeSort(setTemplateSort, setTemplatePage)} />
                  <SortableHeader label="Created By" column="createdBy" sort={templateSort} onSort={changeSort(setTemplateSort, setTemplatePage)} />
                  <SortableHeader label="Created On" column="createdAt" sort={templateSort} onSort={changeSort(setTemplateSort, setTemplatePage)} />
                  <th><span className="nw-static-header">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {pagedTemplates.map((item) => (
                  <tr key={item.id} className="communications-history-row" role="button" tabIndex="0" aria-label={`Open template details for ${item.title}`} onClick={() => setSelectedTemplate(item)} onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedTemplate(item); }
                  }}>
                    <td>{item.title}</td>
                    <td>{item.subject}</td>
                    <td><span className="communications-content-preview">{emailBodyText(item.body)}</span></td>
                    <td>{item.createdBy || "Administrator"}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      {canDeleteCommunications ? (
                        <button
                          type="button"
                          className="app-table-action-button is-delete"
                          aria-label={`Delete template ${item.title}`}
                          title="Delete template"
                          onClick={(event) => { event.stopPropagation(); deleteTemplate(item.id); }}
                        >
                          <FaTrash aria-hidden="true" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {!pagedTemplates.length ? (
                  <tr>
                    <td className="communications-table-empty" colSpan="6">
                      No email templates found with the current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            </div>
            <Pagination currentPage={templatePage} totalItems={sortedTemplates.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setTemplatePage} scrollToTopOnChange={false} />
          </div>
        </>
      )}
      </div>
      </section>
      {selectedHistory ? (
        <div
          className="email-history-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedHistory(null);
          }}
        >
          <section
            className="email-history-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-history-title"
          >
            <header className="email-history-modal-header">
              <div>
                <span>Email History Details</span>
                <h3 id="email-history-title">{selectedHistory.subject}</h3>
              </div>
              <button
                type="button"
                data-no-standard-button="true"
                className="app-window-close email-history-close"
                aria-label="Close email details"
                title="Close"
                onClick={() => setSelectedHistory(null)}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </header>
            <div className="email-history-modal-content">
              <dl className="email-history-detail-grid">
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span
                      className={`communication-status ${selectedHistory.status}`}
                    >
                      {selectedHistory.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Date sent</dt>
                  <dd>{formatDate(selectedHistory.sentAt)}</dd>
                </div>
                <div>
                  <dt>Created on</dt>
                  <dd>{formatDate(selectedHistory.createdAt)}</dd>
                </div>
                <div>
                  <dt>Created by</dt>
                  <dd>{selectedHistory.createdBy || "Administrator"}</dd>
                </div>
                <div className="wide">
                  <dt>Recipient(s)</dt>
                  <dd>
                    {(selectedHistory.recipients || []).join(", ") || "-"}
                  </dd>
                </div>
                <div>
                  <dt>From</dt>
                  <dd>{selectedHistory.fromAddresses || "-"}</dd>
                </div>
                <div>
                  <dt>Reply-To</dt>
                  <dd>{selectedHistory.replyTo || "-"}</dd>
                </div>
                <div>
                  <dt>CC</dt>
                  <dd>{selectedHistory.cc || "-"}</dd>
                </div>
                <div>
                  <dt>Reply-To group</dt>
                  <dd>
                    {selectedHistory.replyGroupName
                      ? `${selectedHistory.replyGroupName}${selectedHistory.replyGroupEmail ? ` — ${selectedHistory.replyGroupEmail}` : ""}`
                      : "-"}
                  </dd>
                </div>
              </dl>
              <section className="email-history-section">
                <h4>Message</h4>
                <div className="email-history-message">
                  {emailBodyText(selectedHistory.body)}
                </div>
              </section>
              <section className="email-history-section">
                <h4>
                  <FaPaperclip aria-hidden="true" /> Attachments
                </h4>
                {(selectedHistory.attachments || []).length ? (
                  <div className="email-history-attachments">
                    {selectedHistory.attachments.map((file, index) => (
                      <div
                        className="email-history-attachment"
                        key={`${file.name}-${index}`}
                      >
                        <FaPaperclip aria-hidden="true" />
                        <div>
                          <strong>{file.name}</strong>
                          <span>
                            {formatFileSize(file.size)}
                            {file.type ? ` · ${file.type}` : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="email-history-none">No attachments.</p>
                )}
              </section>
              {selectedHistory.error ? (
                <section className="email-history-section email-history-error">
                  <h4>Delivery error</h4>
                  <p>{selectedHistory.error}</p>
                </section>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
      {selectedTemplate ? (
        <div className="email-history-modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelectedTemplate(null);
        }}>
          <section className="email-history-modal" role="dialog" aria-modal="true" aria-labelledby="email-template-title">
            <header className="email-history-modal-header">
              <div>
                <span>Email Template Details</span>
                <h3 id="email-template-title">{selectedTemplate.title}</h3>
              </div>
              <button type="button" data-no-standard-button="true" className="app-window-close email-history-close" aria-label="Close template details" title="Close" onClick={() => setSelectedTemplate(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </header>
            <div className="email-history-modal-content">
              <dl className="email-history-detail-grid">
                <div className="wide"><dt>Subject</dt><dd>{selectedTemplate.subject || "-"}</dd></div>
                <div><dt>Created by</dt><dd>{selectedTemplate.createdBy || "Administrator"}</dd></div>
                <div><dt>Created on</dt><dd>{formatDate(selectedTemplate.createdAt)}</dd></div>
              </dl>
              <section className="email-history-section">
                <h4>Message Content</h4>
                <div className="email-history-message">{emailBodyText(selectedTemplate.body)}</div>
              </section>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
